import MDBox from "components/MDBox";
import UiSideModal from "components/base/UiSideModal";
import { WorksheetProduct } from "generated/graphql";
import useCreateWorksheetProduct from "hooks/worksheetProducts/useCreateWorksheetProduct";
import useUpdateWorksheetProduct from "hooks/worksheetProducts/useUpdateWorksheetProduct";
import WorksheetItemEditor from "modules/proposals/worksheet/WorksheetItemEditor";
import {
  formValuesToWorksheetProductInput,
  getBlankWorksheetItem,
  getWorksheetProductFormValues,
} from "modules/proposals/worksheet/worksheetFormValues";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { useMemo, useState } from "react";

type WorksheetProductEditorDrawerProps = {
  open: boolean;
  product: WorksheetProduct | null;
  onClose: () => void;
  onSaved: () => void;
};

export default function WorksheetProductEditorDrawer({
  open,
  product,
  onClose,
  onSaved,
}: WorksheetProductEditorDrawerProps) {
  const [createWorksheetProduct, createContext] = useCreateWorksheetProduct();
  const [updateWorksheetProduct, updateContext] = useUpdateWorksheetProduct();
  const [saving, setSaving] = useState(false);

  const defaultValues = useMemo(
    () => (product ? getWorksheetProductFormValues(product) : getBlankWorksheetItem()),
    [product]
  );

  const handleSave = async (values: WorksheetItemInput) => {
    setSaving(true);

    const input = formValuesToWorksheetProductInput(values, { includeLibraryMeta: true });

    let succeeded = false;

    if (product) {
      const result = await updateWorksheetProduct({
        variables: { input: { ...input, id: product.id }, expectedRevision: product.revision },
      });
      succeeded = updateContext.isSuccess(result.data);
    } else {
      const result = await createWorksheetProduct({ variables: { input } });
      succeeded = createContext.isSuccess(result.data);
    }

    setSaving(false);

    if (!succeeded) {
      return;
    }

    onSaved();
    onClose();
  };

  return (
    <UiSideModal
      open={open}
      onClose={onClose}
      title={product ? "Edit Worksheet Product" : "New Worksheet Product"}
      width="900px"
    >
      <MDBox p="16px">
        <WorksheetItemEditor
          key={product?.id ?? "new"}
          defaultValues={defaultValues}
          saving={saving}
          showProductType
          onSave={handleSave}
        />
      </MDBox>
    </UiSideModal>
  );
}
